import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { FiExternalLink, FiGithub } from "react-icons/fi";
import Chip, { ChipRow } from "../common/Chip";
import CaseNotes from "../common/CaseNotes";
import ArchitectureDialog from "../common/ArchitectureDialog";
import { SNAP, staggerContainer, viewport } from "../../utils/motion";

// Text-first cards, two per row so the evidence and actions have room to breathe.

const cardIn = {
  hidden: { opacity: 0, y: 22, scale: 0.97 },
  show: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.42, ease: SNAP },
  },
};

const Grid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 24px;
  align-items: stretch;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled(motion.div)`
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.biome.projects.card};
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 0;
  padding: 26px;

  @media (max-width: 640px) {
    padding: 20px;
  }

  &:hover {
    border-color: ${({ theme }) => theme.orange};
  }
`;

const Title = styled.h4`
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: 21px;
  font-weight: 900;
  line-height: 1.15;
  color: ${({ theme }) => theme.biome.projects.ink};
  margin: 0 0 10px;
`;

const Desc = styled.p`
  font-size: 14px;
  line-height: 1.6;
  color: ${({ theme }) => theme.biome.projects.muted};
  margin: 0;
`;

const Foot = styled.div`
  margin-top: auto;
  padding-top: 24px;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 14px;
`;

const Action = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 0;
  border: 1px solid rgba(255, 255, 255, 0.35);
  background: transparent;
  color: ${({ theme }) => theme.biome.projects.ink};
  text-decoration: none;

  font-family: ${({ theme }) => theme.fonts.ui};
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  transition: background 120ms linear, color 120ms linear;

  &:hover {
    background: ${({ theme }) => theme.orange};
    border-color: ${({ theme }) => theme.orange};
    color: ${({ theme }) => theme.frameInk};
  }
`;

const OtherProjects = ({ projects }) => (
  <Grid
    variants={staggerContainer(0.06)}
    initial="hidden"
    whileInView="show"
    viewport={viewport}
  >
    {projects.map((project) => (
      <Card key={project.id} variants={cardIn}>
        <Title>{project.title}</Title>
        <Desc>{project.description}</Desc>
        <CaseNotes project={project} />

        <Foot>
          <ChipRow>
            {project.tags?.map((tag) => (
              <Chip key={tag} $tone="projects">
                {tag}
              </Chip>
            ))}
          </ChipRow>

          <Actions>
            <ArchitectureDialog project={project} />
            {project.github && (
              <Action href={project.github} target="_blank" rel="noreferrer">
                <FiGithub /> Code
              </Action>
            )}
            {project.webapp && (
              <Action href={project.webapp} target="_blank" rel="noreferrer">
                <FiExternalLink /> Live
              </Action>
            )}
          </Actions>
        </Foot>
      </Card>
    ))}
  </Grid>
);

export default OtherProjects;
